export const INVALID_SPEED = 9999;

export interface GpsTrace {
    timestamp: number;
    lat: number;
    lng: number;
    speed: number;
}

export const IC_EVENTS_LIST: string[] = [
    'MsgEyesOffRoad',
    'MsgSleep',
    'MsgRecurringEOR',
    'MsgInCabinCameraCovered',
    'MsgDisconnectedCamera',
    'Cellphone',
    'MsgCellPhoneUse',
];

export const EVENTS_WITHOUT_VIDEO: string[] = ['GeofenceEnter', 'GeofenceLeave'];

/** Lowercased `profile.customer.name` for pending crash incidents on Events and related actions (dismiss / report). */
export const CUSTOMERS_WITH_PENDING_CRASH_INCIDENTS_CONTROL: string[] = ['qa-il', 'metropoline'];

const EVENT_TYPE_MAP: Record<string, string> = {
    MsgEyesOffRoad: 'content.events.types.eor',
    MsgSleep: 'content.events.types.sleep',
    MsgTailgatingDetection: 'content.events.types.tailgate',
    MsgPublishVideo: 'content.events.types.continuous',
    MsgPublishBrokenVideo: 'content.events.types.broken_continuous',
    MsgDriverButtonPressed: 'content.events.types.button_pressed',
    MsgHardbrake: 'content.events.types.hard_brake',
    ManualEvent: 'content.events.types.manual_event',
    ManualClipRequest: 'content.events.types.clip_request',
    GeofenceEnter: 'content.events.types.geofence_enter',
    GeofenceLeave: 'content.events.types.geofence_leave',
    MsgRecurringTG: 'content.events.types.recurring_tailgate',
    MsgRecurringEOR: 'content.events.types.recurring_eyes_off_road',
    MsgImpact: 'content.events.types.impact',
    MsgCrashIncident: 'content.events.types.crash_incident',
    MsgCollisionTrackDetection: 'content.events.types.collision_track',
    MsgSpeedingDetection: 'content.events.types.speeding',
    MsgInCabinCameraCovered: 'content.events.types.in_cabin_camera_covered',
    MsgNearCollision: 'content.events.types.near_collision',
    Cellphone: 'content.events.types.cellphone',
    MsgCellPhoneUse: 'content.events.types.cellphone',
    MsgCameraBlocked: 'content.events.types.camera_blocked',
    MsgCameraNoFace: 'content.events.types.camera_no_face',
    MsgParkingModeEnabled: 'content.events.types.parking_mode_enabled',
    MsgParkingModeDisabled: 'content.events.types.parking_mode_disabled',
    ConfigUpload: 'ConfigUpload',
    MsgImproperShutdown: 'MsgImproperShutdown',
    MsgPublishDebugFiles: 'MsgPublishDebugFiles',
    MsgSkipFrames: 'MsgSkipFrames',
    MsgDisconnectedCamera: 'MsgDisconnectedCamera',
    MsgErrorCamera: 'MsgErrorCamera',
    MsgFrontFacingCamUnwell: 'MsgFrontFacingCamUnwell',
    MsgFrontFacingCamHigh: 'MsgFrontFacingCamHigh',
    MsgFrontFacingCamLow: 'MsgFrontFacingCamLow',
    MsgPOSTFailure: 'MsgPOSTFailure',
    MsgScheduledReboot: 'MsgScheduledReboot',
    MsgGetInternalDb: 'MsgGetInternalDb',
    MsgGetInternalFile: 'MsgGetInternalFile',
};

// Motive event types (official names)
const MOTIVE_EVENT_TYPE_MAP: Record<string, string> = {
    hard_brake: 'content.events.types.hard_brake',
    hard_corner: 'content.events.types.hard_turn',
    crash: 'content.events.types.crash_incident',
    seat_belt_violation: 'content.events.types.seat_belt_violation',
    tailgating: 'content.events.types.tailgate',
    cell_phone: 'content.events.types.cellphone',
    stop_sign_violation: 'content.events.types.stop_sign_violation',
    distraction: 'content.events.types.distracted_driving',
    unsafe_lane_change: 'content.events.types.unsafe_lane_change',
    drowsiness: 'content.events.types.drowsiness',
    road_facing_cam_obstruction: 'content.events.types.road_facing_cam_obstruction',
    driver_facing_cam_obstruction: 'content.events.types.driver_facing_cam_obstruction',
    forward_collision_warning: 'content.events.types.forward_collision_warning',
    manual_event: 'content.events.types.manual_event',
    unsafe_parking: 'content.events.types.unsafe_parking',
    hard_acceleration: 'content.events.types.hard_acceleration',
    hard_turn: 'content.events.types.hard_turn',
    distracted_driving: 'content.events.types.distracted_driving',
    following_distance: 'content.events.types.following_distance',
    speeding: 'content.events.types.speeding',
};

export const COMBINED_EVENT_TYPE_MAP: Record<string, string> = {
    ...EVENT_TYPE_MAP,
    ...MOTIVE_EVENT_TYPE_MAP,
};

/** Keys that share a value become one entry: sorted keys joined by commas → that value. */
function groupMapKeysBySharedValue(map: Record<string, string>): Record<string, string> {
    const valueToKeys = new Map<string, string[]>();
    for (const [key, value] of Object.entries(map)) {
        const keys = valueToKeys.get(value);
        if (keys) keys.push(key);
        else valueToKeys.set(value, [key]);
    }
    const out: Record<string, string> = {};
    for (const [value, keys] of valueToKeys) {
        if (keys.length === 1) {
            out[keys[0]] = value;
        } else {
            out[[...keys].sort().join(',')] = value;
        }
    }
    return out;
}

export const getEventTypeMap = (provider?: string | null): Record<string, string> => {
    if (!!provider && provider === 'motive') {
        return MOTIVE_EVENT_TYPE_MAP;
    }

    return groupMapKeysBySharedValue(EVENT_TYPE_MAP);
};
